/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Security Service - SuperAdmin Security Overview API
 * ============================================================================
 */

import { get, post } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';

const BASE_URL = '/admin/security';

export interface IpFailureSummary {
  ipAddress: string;
  failureCount: number;
  lastAttemptAt: string | null;
}

export interface UserFailureSummary {
  userId: string;
  email: string;
  failureCount: number;
  isLocked: boolean;
}

export interface OrgSessionCount {
  orgId: string;
  orgName: string;
  sessionCount: number;
}

export interface LockedAccount {
  userId: string;
  email: string;
  lockedAt: string | null;
  unlockAt: string | null;
  failedAttempts: number;
}

export interface SecurityOverview {
  failedLogins24h: number;
  failedLogins7d: number;
  lockedAccountsCount: number;
  activeSessionsCount: number;
  mfaAdoptionRate: number;
  topFailedIps: IpFailureSummary[];
  topFailedUsers: UserFailureSummary[];
  sessionsByOrg: OrgSessionCount[];
  lockedAccounts: LockedAccount[];
}

export interface FailedLoginSummary {
  byIp: IpFailureSummary[];
  byUser: UserFailureSummary[];
}

export const securityService = {
  async getOverview(): Promise<SecurityOverview> {
    const response = await get<ApiResponse<SecurityOverview>>(`${BASE_URL}/overview`);
    return response.data;
  },

  async getFailedLogins(): Promise<FailedLoginSummary> {
    const response = await get<ApiResponse<FailedLoginSummary>>(`${BASE_URL}/failed-logins`);
    return response.data;
  },

  async getLockedAccounts(): Promise<LockedAccount[]> {
    const response = await get<ApiResponse<LockedAccount[]>>(`${BASE_URL}/locked-accounts`);
    return response.data;
  },

  async getSessionsByOrg(): Promise<OrgSessionCount[]> {
    const response = await get<ApiResponse<OrgSessionCount[]>>(`${BASE_URL}/sessions`);
    return response.data;
  },

  async unlockAccount(userId: string): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/accounts/${userId}/unlock`, {});
  },
};

export default securityService;
